import { useId } from 'react'
import { APP_NAME } from '@/lib/constants'
import { cn } from '@/lib/utils'

interface LogoProps {
  /** `mark` is the two-part symbol alone, for tight spaces like a loading screen. */
  variant?: 'wordmark' | 'mark'
  /** White lettering, for painted scenes that are dark in both themes. */
  onDark?: boolean
  className?: string
}

/** The mark: the cool half (handled) over the warm half (urgent). */
function MarkPaths({ cool, warm }: { cool: string; warm: string }) {
  return (
    <>
      <defs>
        <linearGradient id={cool} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#1436a8" />
          <stop offset=".6" stopColor="#0e9bc4" />
          <stop offset="1" stopColor="#35e0c4" />
        </linearGradient>
        <linearGradient id={warm} x1="0" y1="1" x2="1" y2="0">
          <stop offset=".3" stopColor="#ff3d6e" />
          <stop offset=".9" stopColor="#ff8a3d" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${cool})`}
        d="M30.2,16h5.1V0h-9.8C12.5.7,4.3,8,1.2,18.9.4,21.9,0,24.9,0,28v4.9h17.7v-3.2c0-1.5.1-2.9.5-4.3,1.4-6.4,5.4-9.2,12-9.4Z"
      />
      <path
        fill={`url(#${warm})`}
        d="M35.3,16.7v5.2c0,5.6-.5,9.3-7.9,10.9-1.8.4-3.6.6-5.4.6h-4.3s0,11.8,0,11.8l11.1,12.1h20.7l-13.5-14.6h0c8.2-2.4,16.5-6.9,16.9-16.9v-9.1h-17.7Z"
      />
    </>
  )
}

/**
 * The RingOut logo, ported from the console design (`.side-head .logo`). The
 * lettering takes `currentColor`, so it follows the theme; the mark keeps its two
 * gradients in both. Inline SVG rather than an image, so it is crisp at any size.
 * Size it with a height class (`h-8`); the width follows the viewBox.
 */
export function Logo({ variant = 'wordmark', onDark = false, className }: LogoProps) {
  // Gradient ids must be unique on the page: the logo can appear more than once.
  const id = useId().replace(/[^\w-]/g, '')
  const cool = `${id}-cool`
  const warm = `${id}-warm`

  if (variant === 'mark') {
    return (
      <svg viewBox="0 0 53 58" role="img" aria-label={APP_NAME} className={cn('size-8', className)}>
        <MarkPaths cool={cool} warm={warm} />
      </svg>
    )
  }

  return (
    <svg
      viewBox="0 0 266 73"
      role="img"
      aria-label={APP_NAME}
      className={cn('logo w-auto flex-none', onDark && 'text-white', className)}
    >
      <g className="logo-word">
        <path
          fill="currentColor"
          d="M78.9,49.1v-8.9h.7c4.6,0,7.8-2.7,7.8-6.7s-3.1-6.7-7.7-6.7-7.7,2.7-7.7,6.7v24.7h-9.7v-24.7c0-9.5,7-15.8,17.4-15.8s17.4,6.3,17.4,15-7.5,12.2-10.2,12.2v.6c2.6,0,5.6,2,6.9,4l5.7,8.9h-11.1l-4.3-6.6c-1-1.5-2.7-2.5-4.5-2.5h-.7Z"
        />
        <path
          fill="currentColor"
          d="M108,17.6c3.3,0,5.4,1.9,5.4,4.9s-2.2,4.9-5.4,4.9-5.4-1.9-5.4-4.9,2.2-4.9,5.4-4.9ZM103.2,29.6h9.7v28.6h-9.7v-28.6Z"
        />
        <path
          fill="currentColor"
          d="M126.7,58.2h-9.7v-14.7c0-8.6,5.8-14.4,14.7-14.4s14.4,5.8,14.4,14.4v14.7h-9.7v-15.2c0-2.9-1.9-4.8-4.9-4.8s-4.8,1.9-4.8,4.8v15.2Z"
        />
        <path
          fill="currentColor"
          d="M163.9,29c8.5,0,14.2,5.3,14.2,12.8s-4.9,9.7-7.1,9.7v.6c3,0,7.4,2.4,7.4,8.5s-5.7,12.5-14.2,12.5-14.2-5.4-14.2-13.4h9.7c0,2.6,1.8,4.3,4.5,4.3s4.5-1.7,4.5-4.3-1.4-4.3-4.7-4.3c-8.5,0-14.2-5.3-14.2-13.1s5.7-13.1,14.2-13.1ZM163.9,46.2c3.8,0,4.5-1.6,4.5-4s-1.8-4-4.5-4-4.5,1.6-4.5,4,1.8,4,4.5,4ZM176.8,20.6c3.3,0,5.4,1.9,5.4,4.9s-2.2,4.9-5.4,4.9-5.4-1.9-5.4-4.9,2.2-4.9,5.4-4.9Z"
        />
        <path
          fill="currentColor"
          d="M196.8,29c9,0,15,5.9,15,14.8s-6,14.8-15,14.8-15-5.9-15-14.8,6-14.8,15-14.8ZM196.8,49.6c3.2,0,5.3-2.3,5.3-5.7s-2.1-5.7-5.3-5.7-5.3,2.3-5.3,5.7,2.1,5.7,5.3,5.7Z"
        />
        <path
          fill="currentColor"
          d="M230,49.6c2.9,0,4.9-1.9,4.9-4.8v-15.2h9.7v14.7c0,8.6-5.8,14.4-14.4,14.4s-14.7-5.8-14.7-14.4v-14.7h9.7v15.2c0,2.9,1.9,4.8,4.8,4.8Z"
        />
        <path
          fill="currentColor"
          d="M248.2,36.9v-13.3h9.7v6.3h8.1v8.6h-8.1c0,6.6,1.9,9.9,8.1,9.9v9.7c-12.7,0-17.8-7.5-17.8-21.2Z"
        />
      </g>
      <MarkPaths cool={cool} warm={warm} />
    </svg>
  )
}
